import { memo, MouseEventHandler } from 'react';
import { BarStack as VisxBarStack, BarRounded } from '@visx/shape';
import { ScaleLinear, ScaleTime } from 'd3-scale';
import { Text } from '@visx/text';

import { generateShortId } from '@/lib/utils/string';

import { isGroupedTimeSeriesData, SeriesMetaData, TimeSeriesData } from '../grouped-chart/types';
import {
  generateStackedAndGroupedChartDataByKeys,
  generateStackedChartDataByKeys,
  mergeStackedSeries,
} from './utils';

import commonStyles from '../charts.module.scss';

type BarStackProps = {
  data: TimeSeriesData;
  keys: string[];
  group?: string;
  valueScale: ScaleLinear<number, number>;
  dateScale: ScaleTime<number, number>;
  offset: number;
  barWidth: number;
  highlightKey?: string;
  onMouseMove?: MouseEventHandler<Element>;
  onMouseLeave?: MouseEventHandler<Element>;
};

interface StackedTimeSeriesData {
  minValue: number;
  maxValue: number;
  series: SeriesMetaData[];
  items: {
    date: Date;
    groupLabel?: string;
    stackTotal: number;
  }[];
}

const getStackedData = (
  data: TimeSeriesData,
  keys: string[],
  group?: string,
): StackedTimeSeriesData =>
  isGroupedTimeSeriesData(data)
    ? generateStackedAndGroupedChartDataByKeys(data, keys, group ?? '')
    : generateStackedChartDataByKeys(data, keys);

export const BarStack = memo(function BarStack(props: BarStackProps) {
  const { data, keys, group } = props;
  const clipPathId = `clip-path-${generateShortId(6)}`;

  const mergedData = mergeStackedSeries(data, keys, 'total');
  const stackedData = getStackedData(data, keys, group);

  return (
    <>
      <defs>
        <clipPath id={clipPathId}>
          <BarStackInner
            {...props}
            data={getStackedData(mergedData, ['total'], group)}
            keys={['total']}
            rounded
          />
        </clipPath>
      </defs>
      <BarStackInner {...props} data={stackedData} clipPathId={clipPathId} />
      <BarStackInnerAxis {...props} data={stackedData} />
    </>
  );
});

type BarStackInnerProps = Omit<BarStackProps, 'data'> & {
  data: StackedTimeSeriesData;
  clipPathId?: string;
};

export const BarStackInner = (props: BarStackInnerProps & { rounded?: boolean }) => {
  const {
    data,
    keys,
    dateScale,
    valueScale,
    clipPathId,
    barWidth,
    highlightKey,
    rounded = false,
  } = props;

  return (
    <VisxBarStack
      keys={keys}
      data={data.items}
      x={(d) => d.date}
      xScale={dateScale}
      yScale={valueScale}
      order="reverse"
      color={(bar) => data.series.find(({ key }) => key === bar)?.color ?? ''}>
      {(barStacks) =>
        barStacks.map((barStack, barStackIdx) =>
          barStack.bars.map((bar) => {
            if (bar.height === 0) {
              return null;
            }
            const barHeight = Math.max(1, bar.height); // very small clip paths misbehave in chrome, see SUP-3580
            const isHovered = highlightKey === barStack.key;
            const hasSomeSeriesHovered = highlightKey !== undefined;
            const fillOpacity = hasSomeSeriesHovered && !isHovered ? 0.3 : 0.9;

            return (
              <BarRounded
                key={`barstack-${barStack.index}-${bar.index}`}
                radius={rounded ? 5 : 0}
                x={dateScale(bar.bar.data.date) - barWidth / 2}
                y={bar.y}
                width={barWidth}
                height={barHeight}
                fill={bar.color}
                fillOpacity={fillOpacity}
                top={barStackIdx === barStacks.length - 1}
                clipPath={clipPathId !== undefined ? `url(#${clipPathId})` : undefined}
                onMouseMove={props.onMouseMove}
                onMouseLeave={props.onMouseLeave}
              />
            );
          }),
        )
      }
    </VisxBarStack>
  );
};

type BarStackInnerAxisProps = Omit<BarStackProps, 'data'> & {
  data: StackedTimeSeriesData;
};

export const BarStackInnerAxis = (props: BarStackInnerAxisProps) => {
  const { data, keys, dateScale, valueScale } = props;

  return (
    <VisxBarStack
      keys={keys}
      data={data.items}
      x={(d) => d.date}
      xScale={dateScale}
      yScale={valueScale}
      color={(bar) => data.series.find(({ key }) => key === bar)?.color ?? ''}>
      {(barStacks) =>
        barStacks.map((barStack) =>
          barStack.bars.map((bar) => (
            <Text
              key={`barstackaxis-${barStack.index}-${bar.index}`}
              x={dateScale(bar.bar.data.date)}
              y={valueScale(0) + 5}
              angle={-90}
              fontSize={12}
              className={commonStyles.tickLabel}
              textAnchor="end"
              verticalAnchor="middle"
              width={100}
              scaleToFit="shrink-only">
              {props.group}
            </Text>
          )),
        )
      }
    </VisxBarStack>
  );
};
